import React from "react";
import RainOverlay from "./RainOverlay";
import { SnowOverlay, StarsOverlay, MistOverlay } from "./SceneOverlays";

// Ordered so a user can taste each preset in sequence.
// Two shapes coexist:
//   • gradient preset — a flowing CSS gradient plus drifting orbs.
//   • scene preset    — a still photograph with a subtle ken-burns motion,
//                       optionally layered with a weather overlay (rain, etc.)
//                       that softens over time.
export const LIGHT_PRESETS = [
  {
    id: "flow",
    name: "flow",
    type: "gradient",
    description: "soft twilight, slow drift",
    swatch: "linear-gradient(135deg, #312e81, #0f766e, #2e1065)",
    orbs: ["orb-flow-1", "orb-flow-2", "orb-flow-3"],
  },
  {
    id: "aurora",
    name: "aurora",
    type: "gradient",
    description: "night sky with gentle curtains",
    swatch: "linear-gradient(135deg, #020617, #10b981, #6366f1)",
    orbs: ["orb-aurora-1", "orb-aurora-2", "orb-aurora-3"],
  },
  {
    id: "bokeh",
    name: "bokeh",
    type: "gradient",
    description: "many soft floating lights",
    swatch: "linear-gradient(135deg, #1e1b4b, #7dd3fc, #f0abfc)",
    orbs: [
      "orb-bokeh orb-bokeh-1",
      "orb-bokeh orb-bokeh-2",
      "orb-bokeh orb-bokeh-3",
      "orb-bokeh orb-bokeh-4",
      "orb-bokeh orb-bokeh-5",
      "orb-bokeh orb-bokeh-6",
      "orb-bokeh orb-bokeh-7",
      "orb-bokeh orb-bokeh-8",
    ],
  },
  {
    id: "sunset",
    name: "sunset",
    type: "gradient",
    description: "warm dusk, rose and amber",
    swatch: "linear-gradient(135deg, #3b0764, #f472b6, #fb923c)",
    orbs: ["orb-sunset-1", "orb-sunset-2", "orb-sunset-3"],
  },
  {
    id: "forest-rain",
    name: "forest rain",
    type: "scene",
    description: "misty pines, slow rain",
    // Unsplash — misty pine forest (free-to-use CDN URL, resized for perf).
    imageUrl:
      "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=2000&q=80&auto=format&fit=crop",
    // Small preview swatch derived from the photo palette.
    swatch: "linear-gradient(135deg, #1a2820, #2d3d33, #0f1a14)",
    overlay: "rain",
    // The photo already has a lot of atmosphere, so we dim it slightly rather
    // than letting the raw brightness compete with the quote in the center.
    dimOverlay: "rgba(6, 10, 8, 0.35)",
    coupling: { soundId: "rain" },
  },
  {
    id: "mountain-snow",
    name: "winter sky",
    type: "scene",
    description: "silent pines, drifting flakes",
    imageUrl:
      "https://images.unsplash.com/photo-1483347756197-71ef80e95f73?w=2000&q=80&auto=format&fit=crop",
    swatch: "linear-gradient(135deg, #0f172a, #064e3b, #cbd5e1)",
    overlay: "snow",
    dimOverlay: "rgba(10, 14, 20, 0.28)",
  },
  {
    id: "night-stars",
    name: "night stars",
    type: "scene",
    description: "deep sky, gentle twinkle",
    imageUrl:
      "https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=2000&q=80&auto=format&fit=crop",
    swatch: "linear-gradient(135deg, #020617, #1e1b4b, #312e81)",
    overlay: "stars",
    dimOverlay: "rgba(2, 6, 15, 0.40)",
  },
  {
    id: "ocean-shore",
    name: "ocean shore",
    type: "scene",
    description: "far horizon, soft mist",
    imageUrl:
      "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=2000&q=80&auto=format&fit=crop",
    swatch: "linear-gradient(135deg, #0c4a6e, #7dd3fc, #fef3c7)",
    overlay: "mist",
    dimOverlay: "rgba(6, 20, 30, 0.30)",
    coupling: { soundId: "ocean" },
  },
];

export const getCouplingForPreset = (id) => {
  const p = LIGHT_PRESETS.find((x) => x.id === id);
  return p && p.coupling ? p.coupling : null;
};

export const DEFAULT_PRESET_ID = "flow";

export const getPresetById = (id) =>
  LIGHT_PRESETS.find((p) => p.id === id) || LIGHT_PRESETS[0];

const GradientPreset = ({ preset }) => (
  <>
    <div
      className={`calm-gradient preset-${preset.id}`}
      data-testid="calm-background"
      data-preset={preset.id}
      data-preset-type="gradient"
      aria-hidden="true"
    >
      {preset.orbs.map((cls, i) => (
        <div key={`${preset.id}-${i}`} className={`calm-orb ${cls}`} />
      ))}
    </div>
    <div className="calm-noise" aria-hidden="true" />
  </>
);

const ScenePreset = ({ preset }) => (
  <>
    {/* Still image with a very slow ken-burns pan/zoom. `key` forces a
        remount when the scene changes so the ken-burns animation restarts
        cleanly and doesn't inherit odd transform state. */}
    <div
      key={preset.id}
      className="scene-image"
      data-testid="calm-background"
      data-preset={preset.id}
      data-preset-type="scene"
      style={{ backgroundImage: `url(${preset.imageUrl})` }}
      aria-hidden="true"
    />
    {/* Optional dim veil so text over the scene stays readable. */}
    {preset.dimOverlay && (
      <div
        className="fixed inset-0 z-[1] pointer-events-none"
        style={{ background: preset.dimOverlay }}
        aria-hidden="true"
      />
    )}
    {/* Overlay animations */}
    {preset.overlay === "rain" && <RainOverlay key={`${preset.id}-rain`} />}
    {preset.overlay === "snow" && <SnowOverlay key={`${preset.id}-snow`} />}
    {preset.overlay === "stars" && <StarsOverlay key={`${preset.id}-stars`} />}
    {preset.overlay === "mist" && <MistOverlay key={`${preset.id}-mist`} />}
    <div className="calm-noise" aria-hidden="true" />
  </>
);

export const BackgroundGradient = ({ presetId = DEFAULT_PRESET_ID }) => {
  const preset = getPresetById(presetId);
  if (preset.type === "scene") return <ScenePreset preset={preset} />;
  return <GradientPreset preset={preset} />;
};

export default BackgroundGradient;
